import { parseUTCFromAPIToUnix } from '@/composable/useFormatDateAndHours'
import {
  buildSensorStateSegments,
  DOOR_OPEN_STATE_OPEN,
  type DoorOpenSegment,
} from '@/utils/components/door-open-chart'
import {
  mapDoorOpenVoltageToOpenPeriods,
  parseTimeDoorOpen,
  type IDoorOpenPeriod,
  type IDoorOpenVoltageReading,
} from '@/utils/door-open-periods'

/** Vermelho a 20% para períodos de porta aberta; fechada permanece sem fundo. */
export const DOOR_CORRELATION_OPEN_COLOR = 'rgba(251, 84, 84, 0.2)'

export interface IDoorCorrelationSensorData {
  sensor?: { name?: string; unit_display?: string; sensor_type?: string }
  start_date?: string
  end_date?: string
  readings?: Record<string, unknown>
}

export type DoorCorrelationMarkAreaItem = [
  { xAxis: number; itemStyle: { color: string } },
  { xAxis: number },
]

export interface IDoorCorrelationBackground {
  markAreaData: DoorCorrelationMarkAreaItem[]
  segments: DoorOpenSegment[]
  openColor: string
}

/** Deriva aberturas a partir de pontos binários t/v (0 = aberta, 1 = fechada). */
export function mapBinaryTvToOpenPeriods(
  points: Array<{ t?: string | number; v?: number }>,
  rangeEndMs: number,
): IDoorOpenPeriod[] {
  const sorted = points
    .map((reading) => {
      const time =
        reading.t != null && reading.t !== ''
          ? typeof reading.t === 'number'
            ? reading.t
            : parseUTCFromAPIToUnix(reading.t)
          : NaN
      const status =
        reading.v === 0 ? ('open' as const) : reading.v === 1 ? ('closed' as const) : null
      return { time, status }
    })
    .filter((reading) => Number.isFinite(reading.time) && reading.status != null)
    .sort((a, b) => a.time - b.time)

  if (sorted.length === 0) return []

  const periods: IDoorOpenPeriod[] = []
  let openStart: number | null = null

  for (const reading of sorted) {
    if (reading.status === 'open') {
      if (openStart == null) openStart = reading.time
      continue
    }

    if (openStart != null) {
      periods.push({
        start_date: new Date(openStart).toISOString(),
        end_date: new Date(reading.time).toISOString(),
      })
      openStart = null
    }
  }

  if (openStart != null) {
    periods.push({
      start_date: new Date(openStart).toISOString(),
      end_date: new Date(rangeEndMs).toISOString(),
    })
  }

  return periods
}

function firstBinaryTvChannel(
  readings: Record<string, unknown>,
): Array<{ t?: string | number; v?: number }> {
  for (const [channel, value] of Object.entries(readings)) {
    if (channel === 'time_door_open' || channel === 'period_stats' || channel === 'readings_stat') {
      continue
    }
    if (!Array.isArray(value) || value.length === 0) continue
    const sample = value.find(
      (item) =>
        item &&
        typeof item === 'object' &&
        't' in item &&
        'v' in item &&
        ((item as { v?: number }).v === 0 || (item as { v?: number }).v === 1),
    )
    if (sample) {
      return value as Array<{ t?: string | number; v?: number }>
    }
  }
  return []
}

/** Extrai períodos de porta aberta com fallbacks (time_door_open → voltage+s → t/v binário). */
export function extractDoorOpenPeriods(
  readings: Record<string, unknown> | undefined,
  rangeEndMs: number,
): IDoorOpenPeriod[] {
  if (!readings) return []

  const fromTimeDoorOpen = parseTimeDoorOpen(readings)
  if (fromTimeDoorOpen.length > 0) return fromTimeDoorOpen

  const voltage = readings.door_open_voltage
  if (Array.isArray(voltage) && voltage.length > 0) {
    const fromVoltage = mapDoorOpenVoltageToOpenPeriods(
      voltage as IDoorOpenVoltageReading[],
      rangeEndMs,
    )
    if (fromVoltage.length > 0) return fromVoltage
  }

  const binaryPoints = firstBinaryTvChannel(readings)
  if (binaryPoints.length > 0) {
    return mapBinaryTvToOpenPeriods(binaryPoints, rangeEndMs)
  }

  return []
}

export function buildDoorCorrelationBackground(
  doorData: IDoorCorrelationSensorData | null | undefined,
  rangeStart?: number,
  rangeEnd?: number,
): IDoorCorrelationBackground {
  const start =
    rangeStart ??
    (doorData?.start_date != null ? parseUTCFromAPIToUnix(doorData.start_date) : undefined)
  const end =
    rangeEnd ??
    (doorData?.end_date != null ? parseUTCFromAPIToUnix(doorData.end_date) : undefined)

  if (start == null || end == null || !(end > start)) {
    return {
      markAreaData: [],
      segments: [],
      openColor: DOOR_CORRELATION_OPEN_COLOR,
    }
  }

  const openPeriods = extractDoorOpenPeriods(doorData?.readings, end)
  const periodInputs = openPeriods.map((period) => ({
    y: [period.start_date, period.end_date ?? end] as [string | number, string | number],
  }))

  const segments = buildSensorStateSegments(
    periodInputs,
    (value) => (typeof value === 'number' ? value : parseUTCFromAPIToUnix(value)),
    start,
    end,
  )

  const markAreaData: DoorCorrelationMarkAreaItem[] = segments
    .filter((segment) => segment.state === DOOR_OPEN_STATE_OPEN)
    .map((segment) => [
      {
        xAxis: segment.start,
        itemStyle: { color: DOOR_CORRELATION_OPEN_COLOR },
      },
      { xAxis: segment.end },
    ])

  return {
    markAreaData,
    segments,
    openColor: DOOR_CORRELATION_OPEN_COLOR,
  }
}
